/**
 * Country Repository
 * Handles all country-related API calls
 */

import { apiClient } from '../../client'
import type { PaginatedResponse } from '../role.repository'

export interface Country {
    id: number
    en_name: string
    ar_name?: string | null
    status: number
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date
    ref_code?: string
}

export interface CreateCountryRequest {
    en_name: string
    ar_name?: string
    status?: number
    ref_code?: string

    // Note: created_by is automatically set by backend from logged-in user
}

export interface UpdateCountryRequest {
    en_name?: string
    ar_name?: string
    status?: number
    ref_code?: string

    // Note: updated_by is automatically set by backend from logged-in user
}

class CountryRepository {
    /**
     * Get all countries with pagination
     */
    async getAll(page: number = 1, limit: number = 10, filters?: any): Promise<PaginatedResponse<Country>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        // Add filters if provided
        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }
        }

        return apiClient.get<PaginatedResponse<Country>>(`/countries?${params.toString()}`)
    }

    /**
     * Get country by ID
     */
    async getById(id: number): Promise<Country> {
        return apiClient.get<Country>(`/countries/${id}`)
    }

    /**
     * Create a new country
     */
    async create(data: CreateCountryRequest): Promise<Country> {
        return apiClient.post<Country>('/countries', data)
    }

    /**
     * Update an existing country
     */
    async update(id: number, data: UpdateCountryRequest): Promise<Country> {
        return apiClient.put<Country>(`/countries/${id}`, data)
    }

    /**
     * Delete a country
     */
    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/countries/${id}`)
    }

    /**
     * Export countries to Excel
     */
    async exportToExcel(filters?: { search?: string; status?: string }): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters?.search) {
            params.append('search', filters.search)
        }
        if (filters?.status) {
            params.append('status', filters.status)
        }
          const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000'
        const baseUrl = `${API_BASE_URL}/countries/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url)
    }
}

// Export singleton instance
export const countryRepository = new CountryRepository()

